import { createTracking } from '../createTracking';
import { logDebug } from '../utils';
import { SDK_VERSION } from '../version';
import type { TrackingClient, TrackingInitOptions } from '../types';
import { findSnippetScript, parseScriptConfig } from './parseScriptConfig';
import { bindSpaPageTracking } from './spaPageTracking';

export type AnalyseSDKGlobal = TrackingClient & {
  VERSION: string;
  /** 兼容 snippet 预加载队列：AnalyseSDK.push(['track', 'event', props]) */
  push: (command: SnippetCommand) => void;
  /** 是否已完成自动初始化 */
  ready: boolean;
};

export type SnippetCommand =
  | ['init', Partial<TrackingInitOptions>?]
  | ['track', string, Record<string, unknown>?, ...unknown[]]
  | ['pageView', ...unknown[]]
  | ['pageLeave', ...unknown[]]
  | ['identify', string, Record<string, unknown>?]
  | ['resetIdentity']
  | ['setUserProperties', Record<string, unknown>]
  | ['flush']
  | ['destroy']
  | [string, ...unknown[]];

declare global {
  interface Window {
    AnalyseSDK?: AnalyseSDKGlobal | SnippetCommand[];
  }
}

function applyCommand(client: TrackingClient, command: SnippetCommand): void {
  const [method, ...args] = command;
  const fn = (client as unknown as Record<string, unknown>)[method];
  if (typeof fn === 'function') {
    (fn as (...a: unknown[]) => unknown).apply(client, args);
  }
}

/**
 * 浏览器 snippet 启动：解析 script URL → createTracking → 挂到 window.AnalyseSDK
 */
export function bootstrapSnippet(): AnalyseSDKGlobal {
  const pending = Array.isArray(window.AnalyseSDK) ? ([...window.AnalyseSDK] as SnippetCommand[]) : [];

  const script = findSnippetScript();
  const config = parseScriptConfig(script);

  if (!config.productId) {
    console.error(
      '[AnalyseSDK] missing product id. Use <script src=".../analyse-sdk?id=YOUR_PRODUCT_ID">'
    );
  }

  const client = createTracking({
    productId: config.productId || 'unknown',
    projectId: config.projectId,
    endpoint: config.endpoint,
    environment: config.environment,
    appVersion: config.appVersion,
    debug: config.debug,
    batchSize: config.batchSize,
    flushIntervalMs: config.flushIntervalMs,
  });

  let unbindSpa: (() => void) | undefined;
  if (config.autoPageView) {
    unbindSpa = bindSpaPageTracking(client);
  }

  const originalDestroy = client.destroy.bind(client);
  const api: AnalyseSDKGlobal = {
    ...client,
    VERSION: SDK_VERSION,
    ready: true,
    push(command) {
      applyCommand(api, command);
    },
    destroy() {
      unbindSpa?.();
      unbindSpa = undefined;
      originalDestroy();
      api.ready = false;
    },
  };

  // 覆盖展开后被拷贝的方法，保证 destroy 走上面的包装
  api.destroy = api.destroy.bind(api);
  api.push = api.push.bind(api);

  for (const command of pending) {
    try {
      applyCommand(api, command);
    } catch (err) {
      logDebug(Boolean(config.debug), 'pending command failed', command, err);
    }
  }

  window.AnalyseSDK = api;
  logDebug(Boolean(config.debug), 'snippet ready', {
    productId: config.productId,
    endpoint: config.endpoint,
    autoPageView: config.autoPageView,
    version: SDK_VERSION,
  });

  return api;
}
